import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Stack, Typography } from "@mui/material";

import { Button, Form, FormTextField, Modal } from "@covertree/ui";
import {
  selectPolicy,
  selectPolicyBackendLoading,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Toast } from "@covertree/utils";

import {
  InternalTrackingSubcodeForm,
  internalTrackingSubcodeValidator,
} from "./form";

type InternalTrackingSubcodeProps = {
  onSaveInternalTrackingSubcode(subcode: string): Promise<void>;
};

export function InternalTrackingSubcode({
  onSaveInternalTrackingSubcode,
}: InternalTrackingSubcodeProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { internalTrackingSubcode } = useNewBusinessSelector(selectPolicy)!;
  const loading = useNewBusinessSelector(selectPolicyBackendLoading);

  const defaultValues = useMemo(
    () => ({
      subcode: internalTrackingSubcode ?? "",
    }),
    [internalTrackingSubcode]
  );

  const form = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(internalTrackingSubcodeValidator),
  });

  const {
    reset,
    handleSubmit,
    control,
    formState: { isValid },
  } = form;

  const handleModalOpen = useCallback(() => {
    reset(defaultValues);
    setIsModalOpen(true);
  }, [reset, defaultValues]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSubmit = useCallback(
    async ({ subcode }: InternalTrackingSubcodeForm) => {
      if ((internalTrackingSubcode ?? "") === subcode) {
        handleModalClose();
        return;
      }

      try {
        await onSaveInternalTrackingSubcode(subcode ?? "");
        Toast.showSuccess("Internal tracking subcode saved successfully.");
        handleModalClose();
      } catch (error) {
        Toast.showError("Failed to save internal tracking subcode.");
      }
    },
    [internalTrackingSubcode, handleModalClose, onSaveInternalTrackingSubcode]
  );

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleModalOpen}
        sx={{
          position: { xs: "fixed", md: "static" },
          minWidth: "225px",
          maxWidth: { xs: "280px", md: "100%" },
          mt: { xs: 0, md: 2 },
          mb: { xs: 1, md: 0 },
          transform: { xs: "translate(-50%)", md: "none" },
          bottom: 0,
          left: "50%",
          zIndex: 1,
        }}
        fullWidth>
        Internal Tracking Code
      </Button>
      <Modal
        title="Internal Tracking Code"
        open={isModalOpen}
        maxWidth="md"
        onClose={handleModalClose}
        disableClose={loading}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Typography gutterBottom>
            Please enter your internal subcode for this policy, if applicable.
          </Typography>
          <FormProvider {...form}>
            <FormTextField
              control={control}
              optional
              name="subcode"
              id="internal-tracking-subcode"
              label=""
              hiddenLabel
              disabled={loading}
            />
            <Stack direction="row" spacing={3} mt={4}>
              <Button
                loading={loading}
                disabled={loading || !isValid}
                type="submit">
                Save
              </Button>
              <Button
                disabled={loading}
                onClick={handleModalClose}
                variant="outlined">
                Cancel
              </Button>
            </Stack>
          </FormProvider>
        </Form>
      </Modal>
    </>
  );
}
